import {
  faCheck,
  faCopy,
  faShareNodes,
  faTowerBroadcast,
  faTrashCan,
  faUserPlus,
  faXmark,
} from '@fortawesome/pro-duotone-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useDebounce } from '@uidotdev/usehooks';
import { useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { type CollaboratorSearchResult, searchCollaborators } from '@/api/collaborators';
import { addAlbumCollaborators, lookupImmichUsersByEmail } from '@/api/immich';
import { useTripContext } from '@/contexts/TripContext';
import { Button } from '@/design-system/primitives/Button';
import { SectionEyebrow } from '@/design-system/primitives/SectionEyebrow';
import { TextInput } from '@/design-system/primitives/TextInput';
import type { Trip } from '@/types/trip';
import { twx } from '@/utils/tailwind';

export interface CollaborateModalProps {
  /** Trip to manage collaboration for. `null` keeps the modal closed. */
  trip: Trip | null;
  onClose: () => void;
}

/** Build the `?live=<id>` link using the current origin + path. Every trip is
 *  always server-synced, so the link is always available (no minting step). */
const buildLiveUrl = (tripId: string): string =>
  `${window.location.origin}${window.location.pathname}?live=${tripId}`;

/**
 * One modal for a trip's collaboration. Collaborators are added from a search
 * over registered accounts — trip access is account-gated (`authorizeTrip`), so
 * only people who already have a Trippy Planny account can be added. Adding
 * someone grants view/edit access, emails them a link, and (best-effort) shares
 * the trip's Immich album for photo sync. The link is a convenience shortcut,
 * not an open "anyone can edit" link.
 */
export const CollaborateModal = ({ trip, onClose }: CollaborateModalProps) => {
  const { addCollaborator, removeCollaborator } = useTripContext();
  const [query, setQuery] = useState('');
  const debouncedQuery = useDebounce(query, 250);
  const [results, setResults] = useState<Array<CollaboratorSearchResult>>([]);
  const [searching, setSearching] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const [isFocused, setIsFocused] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [copyState, setCopyState] = useState<'idle' | 'copied' | 'error'>('idle');
  const searchInputRef = useRef<HTMLInputElement>(null);
  const searchInputId = useId();
  const copyTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Reset the search each time a different trip opens the modal, and focus the
  // input so adding a collaborator is one keystroke away.
  useEffect(() => {
    if (!trip) return;
    setQuery('');
    setResults([]);
    const id = window.setTimeout(() => searchInputRef.current?.focus(), 50);
    return () => window.clearTimeout(id);
  }, [trip]);

  // Typeahead over registered accounts, excluding people already added.
  useEffect(() => {
    if (!trip) return;
    const q = debouncedQuery.trim();
    if (q.length < 2) {
      setResults([]);
      setSearching(false);
      return;
    }
    let cancelled = false;
    setSearching(true);
    searchCollaborators(q).then((users) => {
      if (cancelled) return;
      const existing = new Set((trip.collaborators ?? []).map((c) => c.email.toLowerCase()));
      setResults(users.filter((u) => !existing.has(u.email.toLowerCase())));
      setActiveIndex(0);
      setSearching(false);
    });
    return () => {
      cancelled = true;
    };
  }, [debouncedQuery, trip]);

  // Body-scroll lock while open (mirrors PhotoLightbox so iOS doesn't
  // rubber-band the page underneath).
  useEffect(() => {
    if (!trip) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [trip]);

  useEffect(
    () => () => {
      if (copyTimerRef.current) clearTimeout(copyTimerRef.current);
    },
    [],
  );

  if (!trip) return null;

  const collaborators = trip.collaborators ?? [];
  const url = buildLiveUrl(trip.id);
  const showDropdown = isFocused && query.trim().length >= 2;

  const handleSelect = async (user: CollaboratorSearchResult) => {
    if (collaborators.some((c) => c.email.toLowerCase() === user.email.toLowerCase())) return;
    setSubmitting(true);
    try {
      // Best-effort Immich: if the account also has an Immich user, capture its
      // id so their photos sync via the trip's album. Never blocks the add.
      let immichUserId: string | undefined;
      try {
        if (trip.immichAlbumId) {
          const result = await addAlbumCollaborators(trip.immichAlbumId, [user.email]);
          immichUserId = result.added[0]?.userId;
        } else {
          const result = await lookupImmichUsersByEmail([user.email]);
          immichUserId = result.found[0]?.userId;
        }
      } catch (err) {
        console.warn('[collaborate] Immich album share skipped', err);
      }
      addCollaborator(trip.id, {
        email: user.email,
        ...(immichUserId ? { immichUserId } : {}),
        addedAt: Date.now(),
      });
      // Email them a link to open the trip so the owner doesn't send it
      // manually. Fire-and-forget — they already have access.
      void fetch(`/api/trips/${encodeURIComponent(trip.id)}/collaborators/invite`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: user.email }),
      }).catch(() => {});
      setQuery('');
      setResults([]);
      searchInputRef.current?.focus();
    } finally {
      setSubmitting(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Escape') {
      setQuery('');
      setResults([]);
      return;
    }
    if (results.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex((i) => Math.min(i + 1, results.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const user = results[activeIndex] ?? results[0];
      if (user) void handleSelect(user);
    }
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopyState('copied');
    } catch {
      setCopyState('error');
    }
    if (copyTimerRef.current) clearTimeout(copyTimerRef.current);
    copyTimerRef.current = setTimeout(() => setCopyState('idle'), 2000);
  };

  const handleNativeShare = async () => {
    if (typeof navigator.share !== 'function') return;
    try {
      await navigator.share({
        title: trip.name,
        text: `Collaborate on my trip "${trip.name}" in Trippy Planny`,
        url,
      });
    } catch {
      // User cancelled or share unavailable — silent.
    }
  };

  // Portal to body so the modal escapes the trip-selector popover's stacking
  // context (the popover sits inside a transformed ancestor, which turns
  // `fixed` into `absolute`).
  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-label={`Collaborate on ${trip.name}`}
      className="fixed inset-0 z-modal flex items-center justify-center p-4 motion-safe:animate-kawaii-modal-scrim"
      onClick={onClose}
    >
      <div className="absolute inset-0 bg-content-scrim" aria-hidden />
      <div
        onClick={(e) => e.stopPropagation()}
        className="relative w-full max-w-md rounded-2xl bg-white shadow-2xl border border-content-border max-h-[85vh] flex flex-col motion-safe:animate-kawaii-modal-dialog"
      >
        <div className="flex items-start gap-2 p-4 border-b border-content-divider">
          <span
            className="shrink-0 mt-0.5 inline-flex items-center justify-center w-7 h-7 rounded-full bg-quaternary-subtle text-quaternary-emphasized"
            aria-hidden
          >
            <FontAwesomeIcon icon={faTowerBroadcast} style={{ width: 14, height: 14 }} />
          </span>
          <div className="flex-1 min-w-0">
            <SectionEyebrow as="h3">Collaborate</SectionEyebrow>
            <p className="text-sm font-medium text-content-text mt-0.5 truncate">{trip.name}</p>
            <p className="text-xs text-content-subtle leading-snug mt-1">
              Add people to let them view and edit this trip. Changes sync live across everyone's
              devices.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="w-8 h-8 rounded-full hover:bg-content-focal text-content-subtle hover:text-content-text flex items-center justify-center transition-colors"
          >
            <FontAwesomeIcon icon={faXmark} style={{ width: 14, height: 14 }} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {/* Add collaborator — search over registered accounts */}
          <div className="space-y-1.5">
            <label
              htmlFor={searchInputId}
              className="block text-xs font-medium text-content-subtle"
            >
              Add a collaborator
            </label>
            <div className="relative">
              <TextInput
                id={searchInputId}
                ref={searchInputRef}
                type="text"
                size="sm"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={handleKeyDown}
                onFocus={() => setIsFocused(true)}
                // Delay so a result's onClick fires before the dropdown unmounts.
                onBlur={() => window.setTimeout(() => setIsFocused(false), 120)}
                placeholder="Search by name or email…"
                autoComplete="off"
                disabled={submitting}
                className="w-full"
              />
              {showDropdown && (
                <div className="absolute z-10 mt-1 w-full rounded-lg border border-content-border bg-white shadow-lg overflow-hidden">
                  {searching && results.length === 0 ? (
                    <p className="px-3 py-2 text-xs text-content-subtle">Searching…</p>
                  ) : results.length === 0 ? (
                    <p className="px-3 py-2 text-xs text-content-subtle leading-snug">
                      No accounts match. Only people with a Trippy Planny account can be added.
                    </p>
                  ) : (
                    <ul>
                      {results.map((user, index) => (
                        <li key={user.email}>
                          <button
                            type="button"
                            // Keep the input focused so onBlur doesn't beat the click.
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={() => handleSelect(user)}
                            className={twx(
                              'flex w-full items-center gap-2 px-3 py-2 text-left transition-colors',
                              index === activeIndex
                                ? 'bg-secondary-subtle'
                                : 'hover:bg-content-focal',
                            )}
                          >
                            <div className="flex-1 min-w-0">
                              <p className="text-sm text-content-text truncate">{user.name}</p>
                              <p className="text-xs text-content-subtle truncate">{user.email}</p>
                            </div>
                            <FontAwesomeIcon
                              icon={faUserPlus}
                              style={{ width: 12, height: 12 }}
                              className="shrink-0 text-content-subtle"
                            />
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )}
            </div>
            <p className="text-[11px] text-content-disabled leading-snug">
              Only people with a Trippy Planny account can collaborate. They'll get an email link
              and the trip appears in their list; if they're on your Immich, their photos sync too.
            </p>
          </div>

          {/* Current collaborators */}
          <div className="space-y-1">
            <SectionEyebrow as="h3">
              {collaborators.length === 0
                ? 'No collaborators yet'
                : `${collaborators.length} collaborator${collaborators.length === 1 ? '' : 's'}`}
            </SectionEyebrow>
            {collaborators.length > 0 && (
              <ul className="divide-y divide-content-divider border border-content-border rounded-xl overflow-hidden">
                {collaborators
                  .slice()
                  .sort((a, b) => a.addedAt - b.addedAt)
                  .map((c) => (
                    <li key={c.email} className="flex items-center gap-2 px-3 py-2 bg-white">
                      <span className="flex-1 min-w-0 truncate text-sm text-content-text">
                        {c.email}
                      </span>
                      <button
                        type="button"
                        onClick={() => removeCollaborator(trip.id, c.email)}
                        aria-label={`Remove ${c.email}`}
                        title="Remove collaborator"
                        className={twx(
                          'w-7 h-7 rounded-md flex items-center justify-center',
                          'text-content-subtle hover:text-error-main hover:bg-error-badge transition-colors',
                        )}
                      >
                        <FontAwesomeIcon icon={faTrashCan} style={{ width: 12, height: 12 }} />
                      </button>
                    </li>
                  ))}
              </ul>
            )}
          </div>

          {/* Share link — a shortcut for collaborators to jump straight to the trip */}
          <div className="space-y-1.5 pt-2 border-t border-content-divider">
            <SectionEyebrow as="h3">Share link</SectionEyebrow>
            <div className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border border-content-border bg-content-focal">
              <span
                className="flex-1 min-w-0 truncate font-mono text-xs text-content-text"
                title={url}
              >
                {url}
              </span>
              <button
                type="button"
                onClick={handleCopy}
                aria-label="Copy link"
                title="Copy to clipboard"
                className={twx(
                  'shrink-0 inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium transition-colors',
                  copyState === 'copied'
                    ? 'bg-success-badge text-success-text'
                    : copyState === 'error'
                      ? 'bg-error-badge text-error-text'
                      : 'bg-white text-content-text hover:bg-content-background border border-content-border',
                )}
              >
                <FontAwesomeIcon
                  icon={copyState === 'copied' ? faCheck : faCopy}
                  style={{ width: 11, height: 11 }}
                />
                {copyState === 'copied' ? 'Copied' : copyState === 'error' ? 'Failed' : 'Copy'}
              </button>
            </div>
            {typeof navigator !== 'undefined' && typeof navigator.share === 'function' && (
              <Button size="sm" variant="ghost" fullWidth onClick={handleNativeShare}>
                <FontAwesomeIcon icon={faShareNodes} style={{ width: 12, height: 12 }} />
                <span className="ml-1.5">Share via…</span>
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
};
